import { FIELDS } from './constants';
import { genderToNum } from './genderHelper';
import * as MapperHelper from './mapperHelper';

const getPersonalInfo = ({ personalInfo, personalInformation }) => {
  if (personalInformation) {
    const { birthDate, genderSysName } = personalInformation ?? {};

    return {
      [FIELDS.BIRTHDATE]: MapperHelper.dateFormmatter(birthDate),
      [FIELDS.FULL_NAME]: MapperHelper.fioJoin(personalInformation),
      [FIELDS.GENDER]: genderToNum(genderSysName),
    };
  }

  const { birthDate, gender, firstName: name, lastName: surname, middleName } = personalInfo ?? {};
  const { sysName: genderSysName } = gender ?? {};

  return {
    [FIELDS.BIRTHDATE]: MapperHelper.dateFormmatter(birthDate),
    [FIELDS.FULL_NAME]: MapperHelper.fioJoin({ middleName, name, surname }),
    [FIELDS.GENDER]: genderToNum(genderSysName),
  };
};

const getContacts = ({ contacts: { email, mobilePhone } }) => {
  return {
    [FIELDS.EMAIL]: email,
    [FIELDS.MOBILE_PHONE]: mobilePhone,
  };
};

const getPassport = ({
  passport: {
    birthPlace,
    issueDate,
    issuer,
    issuerCode,
    number,
    previousIssueDate,
    previousNumber,
    previousSeries,
    series,
  },
}) => {
  const result = {
    [FIELDS.BIRTH_PLACE]: birthPlace,
    [FIELDS.PASSPORT_CHANGED]: false,
    [FIELDS.PASSPORT_ISSUE_DATE]: MapperHelper.dateFormmatter(issueDate),
    [FIELDS.PASSPORT_ISSUER]: issuer,
    [FIELDS.PASSPORT_ISSUER_CODE]: issuerCode,
    [FIELDS.PASSPORT_SERIES_AND_NUMBER]: `${series}${number}`,
  };

  if (previousIssueDate || previousNumber || previousSeries) {
    Object.assign(result, {
      [FIELDS.PASSPORT_CHANGED]: true,
      [FIELDS.PREVIOUS_PASSPORT_ISSUE_DATE]: MapperHelper.dateFormmatter(previousIssueDate),
      [FIELDS.PREVIOUS_PASSPORT_NUMBER]: previousNumber,
      [FIELDS.PREVIOUS_PASSPORT_SERIES]: previousSeries,
    });
  }

  return result;
};

export const getAddress = ({
  livingAddress: { address: livingAddress, isLivingAddressFromDadata },
  registrationAddress: { address: registrationAddress, isRegistrationAddressFromDadata },
}) => {
  const { fiasValue: livingFiasValue } = livingAddress;
  const { fiasValue: registrationFiasValue, indexNumber: registrationIndexNumber } =
    registrationAddress;

  const result = {
    [FIELDS.LIVING_ADDRESS_EQUALS]: true,
    [FIELDS.REGISTRATION_ADDRESS]: registrationFiasValue,
    [FIELDS.REGISTRATION_ADDRESS_DA_DATA_INFO]: registrationAddress,
    [FIELDS.REGISTRATION_ADDRESS_FROM_DA_DATA]: isRegistrationAddressFromDadata,
    [FIELDS.REGISTRATION_INDEX]: registrationIndexNumber,
  };

  if (livingFiasValue && livingFiasValue !== registrationFiasValue) {
    Object.assign(result, {
      [FIELDS.LIVING_ADDRESS]: livingFiasValue,
      [FIELDS.LIVING_ADDRESS_DA_DATA_INFO]: livingAddress,
      [FIELDS.LIVING_ADDRESS_EQUALS]: false,
      [FIELDS.LIVING_ADDRESS_FROM_DA_DATA]: isLivingAddressFromDadata,
    });
  }

  return result;
};

const getDriverLicense = ({ secondDocument }) => {
  const { driverLicense } = secondDocument ?? {};
  const {
    series,
    number,
    drivingExperience,
    previousNumber,
    previousSeries,
    issueDate,
    startDate,
  } = driverLicense ?? {};

  const drivingLicenseSeriesAndNumber = MapperHelper.joinDriverLicense({ number, series });
  const previousDrivingLicenseSeriesAndNumber = MapperHelper.joinDriverLicense({
    number: previousNumber,
    series: previousSeries,
  });

  const result = {
    [FIELDS.DRIVING_EXPERIENCE]: drivingExperience,
    [FIELDS.DRIVING_LICENSE_IS_FIRST]: true,
    [FIELDS.DRIVING_LICENSE_ISSUE_DATE]: MapperHelper.dateFormmatter(issueDate) || '',
    [FIELDS.DRIVING_LICENSE_SERIES_AND_NUMBER]: drivingLicenseSeriesAndNumber,
    [FIELDS.PREVIOUS_DRIVING_LICENSE_ISSUE_DATE]: '',
    [FIELDS.PREVIOUS_DRIVING_LICENSE_SERIES_AND_NUMBER]: '',
  };

  if (startDate !== issueDate) {
    result[FIELDS.PREVIOUS_DRIVING_LICENSE_ISSUE_DATE] =
      MapperHelper.dateFormmatter(startDate) || '';
  }

  if (drivingLicenseSeriesAndNumber !== previousDrivingLicenseSeriesAndNumber) {
    result[FIELDS.PREVIOUS_DRIVING_LICENSE_SERIES_AND_NUMBER] =
      previousDrivingLicenseSeriesAndNumber;
  }

  if (
    result[FIELDS.PREVIOUS_DRIVING_LICENSE_ISSUE_DATE] ||
    result[FIELDS.PREVIOUS_DRIVING_LICENSE_SERIES_AND_NUMBER]
  ) {
    result[FIELDS.DRIVING_LICENSE_IS_FIRST] = false;
  }

  return result;
};

export const backToFrontCustomer = ({ customer }) => {
  return {
    [FIELDS.ID]: customer.id,
    ...getPersonalInfo(customer),
    ...getContacts(customer),
    ...getPassport(customer),
    ...getAddress(customer),
    ...getDriverLicense(customer),
  };
};

export const backToFrontDriver = ({ customer }) => {
  return {
    [FIELDS.ID]: customer.id,
    ...getPersonalInfo(customer),
    ...getDriverLicense(customer),
  };
};

const getRoleCustomerId = (customers, role) => {
  return Number(
    customers.find((customer) => {
      return customer.roleSysName === role;
    })?.customerId
  );
};

const getMappedFullDataCustomer = (fullDataCustomers, customerId) => {
  const targetCustomer = fullDataCustomers.find(({ id }) => {
    return id === customerId;
  });

  if (!targetCustomer) {
    return null;
  }

  return backToFrontCustomer({
    customer: targetCustomer,
  });
};

const backToFrontMapper = ({ fullDataCustomers, customers }) => {
  const owner_customerId = getRoleCustomerId(customers, 'owner');
  const insurer_customerId = getRoleCustomerId(customers, 'insurer');
  const ownerIsInsurer = insurer_customerId === owner_customerId;
  const ownerIsDriver = customers.some((el) => {
    return el.roleSysName === 'driver' && Number(el.customerId) === owner_customerId;
  });
  const ownerFullData = getMappedFullDataCustomer(fullDataCustomers, owner_customerId);

  const mappedCustomers = { insurer: {} };

  mappedCustomers.owner = { ...ownerFullData, isDriver: ownerIsDriver, isInsurer: ownerIsInsurer };

  let insurerUniqueDataString;
  let driverWhoIsInsurer;

  const allDriversIds = customers
    .filter((customer) => {
      return customer.roleSysName === 'driver';
    })
    .map(({ customerId }) => {
      return Number(customerId);
    });

  const getCustomerUniqueDataString = ({ fio, bday }) => {
    return `${fio}${bday}`;
  };

  if (!ownerIsInsurer) {
    mappedCustomers.insurer = getMappedFullDataCustomer(fullDataCustomers, insurer_customerId);
    mappedCustomers.insurer.errors = {};
  }

  // eslint-disable-next-line prefer-const
  insurerUniqueDataString = getCustomerUniqueDataString(mappedCustomers.insurer);

  // eslint-disable-next-line prefer-const
  driverWhoIsInsurer = fullDataCustomers.find((fullCustomer) => {
    return (
      allDriversIds.includes(fullCustomer.id) &&
      insurerUniqueDataString ===
        getCustomerUniqueDataString(backToFrontCustomer({ customer: fullCustomer }))
    );
  });

  mappedCustomers.owner.kaskoInsurerIsDriver = Boolean(driverWhoIsInsurer);

  const genuineDriversIds = allDriversIds.filter((driverId) => {
    return driverWhoIsInsurer
      ? driverId !== owner_customerId && driverId !== driverWhoIsInsurer.id
      : driverId !== owner_customerId;
  });

  mappedCustomers.drivers = fullDataCustomers
    .filter((el) => {
      return genuineDriversIds.includes(el.id);
    })
    .map((el) => {
      return backToFrontDriver({ customer: el });
    });

  return {
    customers: {
      ...mappedCustomers.owner,
      kaskoInsurer: mappedCustomers.insurer,
      drivers: mappedCustomers.drivers,
    },
    ids: {
      ownerId: mappedCustomers.owner.id,
      kaskoInsurerId: mappedCustomers.insurer?.id,
    },
  };
};

export default backToFrontMapper;
